/**
 * Breakpoint Design Tokens
 * Responsive design breakpoints and media queries
 */

// Breakpoint values (in pixels)
export const breakpointValues = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

// Breakpoint names
export type BreakpointName = keyof typeof breakpointValues;

// Media query helpers
export const mediaQueries = {
  // Min-width media queries (mobile-first approach)
  up: {
    xs: `@media (min-width: ${breakpointValues.xs}px)`,
    sm: `@media (min-width: ${breakpointValues.sm}px)`,
    md: `@media (min-width: ${breakpointValues.md}px)`,
    lg: `@media (min-width: ${breakpointValues.lg}px)`,
    xl: `@media (min-width: ${breakpointValues.xl}px)`,
    '2xl': `@media (min-width: ${breakpointValues['2xl']}px)`,
  },

  // Max-width media queries (desktop-first approach)
  down: {
    xs: `@media (max-width: ${breakpointValues.sm - 1}px)`,
    sm: `@media (max-width: ${breakpointValues.md - 1}px)`,
    md: `@media (max-width: ${breakpointValues.lg - 1}px)`,
    lg: `@media (max-width: ${breakpointValues.xl - 1}px)`,
    xl: `@media (max-width: ${breakpointValues['2xl'] - 1}px)`,
    '2xl': `@media (max-width: 9999px)`,
  },

  // Between breakpoints
  between: {
    'xs-sm': `@media (min-width: ${breakpointValues.xs}px) and (max-width: ${breakpointValues.sm - 1}px)`,
    'sm-md': `@media (min-width: ${breakpointValues.sm}px) and (max-width: ${breakpointValues.md - 1}px)`,
    'md-lg': `@media (min-width: ${breakpointValues.md}px) and (max-width: ${breakpointValues.lg - 1}px)`,
    'lg-xl': `@media (min-width: ${breakpointValues.lg}px) and (max-width: ${breakpointValues.xl - 1}px)`,
    'xl-2xl': `@media (min-width: ${breakpointValues.xl}px) and (max-width: ${breakpointValues['2xl'] - 1}px)`,
  },

  // Only specific breakpoint
  only: {
    xs: `@media (max-width: ${breakpointValues.sm - 1}px)`,
    sm: `@media (min-width: ${breakpointValues.sm}px) and (max-width: ${breakpointValues.md - 1}px)`,
    md: `@media (min-width: ${breakpointValues.md}px) and (max-width: ${breakpointValues.lg - 1}px)`,
    lg: `@media (min-width: ${breakpointValues.lg}px) and (max-width: ${breakpointValues.xl - 1}px)`,
    xl: `@media (min-width: ${breakpointValues.xl}px) and (max-width: ${breakpointValues['2xl'] - 1}px)`,
    '2xl': `@media (min-width: ${breakpointValues['2xl']}px)`,
  },
} as const;

// Container max-widths for each breakpoint
export const containerMaxWidths = {
  xs: '100%',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

// Grid columns for each breakpoint
export const gridColumns = {
  xs: 4,
  sm: 8,
  md: 8,
  lg: 12,
  xl: 12,
  '2xl': 12,
} as const;

// Responsive utilities
export const responsive = {
  // Helper function to create responsive values
  createResponsiveValue: <T>(values: Partial<Record<BreakpointName, T>>) => values,

  // Common responsive patterns
  patterns: {
    // Stack on mobile, row on desktop
    stackToRow: {
      xs: 'column',
      md: 'row',
    },

    // Hide on mobile, show on desktop
    hideOnMobile: {
      xs: 'none',
      md: 'block',
    },

    // Show on mobile, hide on desktop
    showOnMobile: {
      xs: 'block',
      md: 'none',
    },

    // Full width on mobile, constrained on desktop
    fullToConstrained: {
      xs: '100%',
      md: 'auto',
    },

    // Small text on mobile, larger on desktop
    textScale: {
      xs: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
    },

    // Compact spacing on mobile, generous on desktop
    spacingScale: {
      xs: '1rem',
      md: '1.5rem',
      lg: '2rem',
    },
  },
} as const;

// Touch target sizes for mobile accessibility
export const touchTargets = {
  minimum: '44px',  // WCAG minimum touch target size
  comfortable: '48px',
  large: '56px',
} as const;

export type MediaQuery = typeof mediaQueries;
export type ResponsiveValue<T> = Partial<Record<BreakpointName, T>>;
export type ContainerMaxWidth = keyof typeof containerMaxWidths;